import React, { useState, useRef, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { API } from "@/lib/api";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Sparkles, X, Send, Trash2, Loader2, Bot } from "lucide-react";

const SESSION_KEY = "wh_ai_session";

function getSession() {
  let s = localStorage.getItem(SESSION_KEY);
  if (!s) {
    s = (crypto.randomUUID && crypto.randomUUID()) || String(Date.now());
    localStorage.setItem(SESSION_KEY, s);
  }
  return s;
}

const SUGGESTIONS = [
  "Bu haftaki açık görevlerim neler?",
  "Gecikmiş görevlerim var mı?",
  "Bekleyen onayları özetle",
];

export default function AiAssistant() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const sessionRef = useRef(getSession());
  const scrollRef = useRef(null);
  const loadedRef = useRef(false);

  useEffect(() => {
    if (open && !loadedRef.current) {
      loadedRef.current = true;
      api.get(`/ai/assistant/history?session_id=${sessionRef.current}`)
        .then(({ data }) => setMessages(Array.isArray(data) ? data.map((m) => ({ role: m.role, content: m.content })) : []))
        .catch(() => {});
    }
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streaming]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/ai/assistant`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("wh_token")}`,
          "X-Org-Id": localStorage.getItem("wh_org") || "",
        },
        body: JSON.stringify({ message: msg, session_id: sessionRef.current }),
      });
      if (!res.ok || !res.body) throw new Error("stream failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          try {
            const evt = JSON.parse(line.slice(5).trim());
            if (evt.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + evt.delta };
                return copy;
              });
            } else if (evt.error) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: evt.error };
                return copy;
              });
            } else if (evt.done && evt.action) {
              ["tasks", "my-work", "dashboard", "admin-overview", "projects"].forEach((k) =>
                qc.invalidateQueries({ queryKey: [k] }));
            }
          } catch {}
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        if (copy.length && !copy[copy.length - 1].content)
          copy[copy.length - 1] = { role: "assistant", content: "Bir hata oluştu, lütfen tekrar deneyin." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  const clearChat = async () => {
    try { await api.delete(`/ai/assistant/history?session_id=${sessionRef.current}`); } catch {}
    setMessages([]);
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="ai-assistant-fab"
          className="fixed z-40 bottom-20 lg:bottom-6 right-4 lg:right-6 h-14 w-14 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg shadow-indigo-600/30 grid place-items-center transition-transform hover:scale-105 active:scale-95"
          aria-label="AI Asistan"
        >
          <Sparkles className="h-6 w-6" />
        </button>
      )}

      {open && (
        <div
          data-testid="ai-assistant-panel"
          className="fixed z-40 bottom-20 lg:bottom-6 right-2 lg:right-6 left-2 lg:left-auto lg:w-[400px] h-[70vh] lg:h-[560px] max-h-[calc(100vh-6rem)] flex flex-col rounded-2xl border bg-white dark:bg-slate-900 shadow-2xl overflow-hidden animate-in-up"
        >
          {/* Header */}
          <div className="flex items-center gap-2 px-4 h-14 bg-gradient-to-r from-indigo-600 to-violet-600 text-white shrink-0">
            <Bot className="h-5 w-5" />
            <div className="leading-tight">
              <p className="text-sm font-semibold">AI Asistan</p>
              <p className="text-[10px] opacity-80">Claude Sonnet 5</p>
            </div>
            <div className="ml-auto flex items-center gap-1">
              {messages.length > 0 && (
                <Button size="icon" variant="ghost" className="h-8 w-8 text-white hover:bg-white/20" onClick={clearChat} data-testid="ai-assistant-clear">
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
              <Button size="icon" variant="ghost" className="h-8 w-8 text-white hover:bg-white/20" onClick={() => setOpen(false)} data-testid="ai-assistant-close">
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="ai-assistant-messages">
            {messages.length === 0 && (
              <div className="text-center py-6">
                <div className="mx-auto h-12 w-12 rounded-full bg-indigo-100 dark:bg-indigo-950 grid place-items-center mb-3">
                  <Sparkles className="h-6 w-6 text-indigo-600" />
                </div>
                <p className="text-sm font-medium">Merhaba {user?.name?.split(" ")[0] || ""} 👋</p>
                <p className="text-xs text-muted-foreground mt-1 mb-4">İşlerin hakkında bana soru sorabilirsin.</p>
                <div className="space-y-2">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} onClick={() => send(s)} data-testid="ai-assistant-suggestion"
                      className="block w-full text-left text-xs px-3 py-2 rounded-lg border bg-muted/40 hover:bg-muted transition-colors">
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex gap-2 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
                <Avatar className="h-7 w-7 shrink-0">
                  <AvatarFallback className={m.role === "user" ? "bg-slate-700 text-white text-[10px]" : "bg-indigo-600 text-white text-[10px]"}>
                    {m.role === "user" ? (user?.name?.[0] || "S") : <Bot className="h-3.5 w-3.5" />}
                  </AvatarFallback>
                </Avatar>
                <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap break-words ${
                  m.role === "user" ? "bg-indigo-600 text-white rounded-tr-sm" : "bg-muted rounded-tl-sm"
                }`}>
                  {m.content || (streaming && i === messages.length - 1 ? <Loader2 className="h-4 w-4 animate-spin" /> : "")}
                </div>
              </div>
            ))}
          </div>

          {/* Input */}
          <div className="border-t p-3 shrink-0">
            <div className="flex items-end gap-2">
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Bir soru yaz..."
                rows={1}
                className="resize-none min-h-[40px] max-h-24"
                data-testid="ai-assistant-input"
              />
              <Button size="icon" onClick={() => send()} disabled={streaming || !input.trim()} data-testid="ai-assistant-send" className="shrink-0">
                {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
